import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronDown, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supportedLanguages, type SupportedLanguage } from "@/lib/i18n";
import { getEquivalentUrl } from "@/lib/slugUtils";

interface LanguageSwitcherProps {
  onLanguageChange?: () => void;
}

const LanguageSwitcher = ({ onLanguageChange }: LanguageSwitcherProps = {}) => {
  const { i18n, t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  // Ensure we have a valid language, fallback to 'en' if not
  const currentLanguage = (supportedLanguages.includes(i18n.language as SupportedLanguage) 
    ? i18n.language 
    : 'en') as SupportedLanguage;

  const changeLanguage = (newLanguage: SupportedLanguage) => {
    const newPath = getEquivalentUrl(location.pathname, newLanguage);
    navigate(newPath);
    i18n.changeLanguage(newLanguage);
    setIsOpen(false);
    onLanguageChange?.();
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 text-primary-foreground hover:text-accent hover:bg-primary-foreground/10 border border-primary-foreground/20">
          <Globe className="w-4 h-4" />
          <span className="hidden sm:inline">{t(`languages.${currentLanguage}`) || currentLanguage}</span>
          <span className="sm:hidden">{currentLanguage.toUpperCase()}</span>
          <ChevronDown className="w-3 h-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {supportedLanguages.map((language) => (
          <DropdownMenuItem
            key={language}
            onClick={() => changeLanguage(language)}
            className={currentLanguage === language ? "bg-accent" : ""}
          >
            {t(`languages.${language}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;